import clsx from 'clsx';
import * as d3 from 'd3';
import { useEffect, useMemo, useRef } from 'react';

import {
  IndexedTsPoint,
  SpxTsData,
} from '../../charts/band-chart/BandChart.types';
import styles from './ChartXAxis.module.scss';
import {
  createChartXAxisDateScale,
  getChartXAxisDateDomain,
  getChartXAxisTickValues,
} from './chart-x-axis-ticks';

export type ChartXAxisCalendarStep = 'month' | 'week';

interface ChartXAxisCommonProps {
  yScale: d3.ScaleLinear<number, number>;
  padding?: number;
  showLine?: boolean;
  y1?: number;
  adjustedWidth?: number;
  isBordered?: boolean;
  locale?: string;
  calendarStep?: ChartXAxisCalendarStep;
}

interface ChartXAxisComputedTicksProps {
  /** Pre-computed tick dates for layout that depends on tick presence before render. */
  tickValues: Date[];
  dateScale: d3.ScaleTime<number, number>;
}

interface ChartXAxisDataProps {
  tsData: IndexedTsPoint[] | SpxTsData[];
  /** Pixel range the ticks are mapped onto, i.e. the chart's x-scale range. */
  xRange: number[];
  domainLimited?: boolean;
}

export type ChartXAxisProps = ChartXAxisCommonProps &
  (ChartXAxisComputedTicksProps | ChartXAxisDataProps);

const TICK_STUB_SIZE = 10;
const INNER_TICK_STUB = 4;

export const ChartXAxis = (props: ChartXAxisProps) => {
  const {
    yScale,
    padding = 0,
    showLine = true,
    y1 = 0,
    adjustedWidth = 0,
    isBordered,
    locale,
    calendarStep = 'month',
  } = props;

  const axisRef = useRef<SVGGElement>(null);

  const overrideTickValues =
    'tickValues' in props ? props.tickValues : undefined;
  const overrideDateScale = 'tickValues' in props ? props.dateScale : undefined;
  const {
    tsData = undefined,
    domainLimited = undefined,
    xRange = undefined,
  } = 'tsData' in props
    ? {
        tsData: props.tsData,
        domainLimited: props.domainLimited,
        xRange: props.xRange,
      }
    : {};

  const dateScale = useMemo(() => {
    if (overrideDateScale) {
      return overrideDateScale;
    }

    return createChartXAxisDateScale(
      getChartXAxisDateDomain(tsData!, domainLimited),
      xRange!
    );
  }, [overrideDateScale, tsData, xRange, domainLimited]);

  const tickValues = useMemo(() => {
    if (overrideTickValues) {
      return overrideTickValues;
    }

    return getChartXAxisTickValues(dateScale, calendarStep);
  }, [overrideTickValues, dateScale, calendarStep]);

  const yTranslate = Math.max(0, yScale.range()[0] + padding);

  useEffect(() => {
    if (axisRef.current && dateScale.range()[1] > 0) {
      const axisSelection = d3.select(axisRef.current);
      const axis = showLine
        ? d3.axisBottom<Date>(dateScale).tickSizeInner(TICK_STUB_SIZE)
        : d3.axisTop<Date>(dateScale).tickSizeInner(INNER_TICK_STUB);

      const formatter =
        calendarStep === 'week'
          ? (date: Date) => {
              const day = date.toLocaleString(locale, { day: 'numeric' });
              const month = date.toLocaleString(locale, { month: 'short' });
              return `${day} ${month}`;
            }
          : (date: Date) => date.toLocaleString(locale, { month: 'short' });

      axis
        .tickValues(tickValues)
        .tickSizeOuter(0)
        .tickPadding(8)
        .tickFormat(formatter);

      axisSelection.call(axis);
    }
  }, [tickValues, dateScale, axisRef, showLine, locale, calendarStep]);

  return (
    <>
      {showLine &&
        tickValues.map(date => {
          const dateXPlot = dateScale(date);
          return (
            <g key={dateXPlot} transform={`translate(0, 0)`}>
              <line
                className={styles.axisLine}
                data-testid="x-axis-line"
                data-axis-line
                y1={y1}
                x1={dateXPlot}
                y2={yScale(yScale.domain()[0]) + TICK_STUB_SIZE + padding}
                x2={dateXPlot}
              />
            </g>
          );
        })}

      {isBordered && !!tickValues.length && (
        <>
          <line
            className={styles.axisBorderLine}
            x1={0}
            x2={adjustedWidth}
            y1={yScale(yScale.domain()[0]) + TICK_STUB_SIZE + padding}
            y2={yScale(yScale.domain()[0]) + TICK_STUB_SIZE + padding}
          />

          <line
            className={styles.axisBorderLine}
            x1={0}
            x2={0}
            y1={y1}
            y2={yScale(yScale.domain()[0]) + TICK_STUB_SIZE + padding}
          />
        </>
      )}

      <g
        className={clsx(styles.chartAxisG, {
          [styles.showLine]: showLine,
        })}
        ref={axisRef}
        data-testid="x-axis"
        transform={`translate(0, ${yTranslate})`}
      />
    </>
  );
};
